// ONE SOURCE OF TRUTH for a webinar's headline metrics, used by BOTH the Webinars tab (Sales Metrics + On-Webinar cards) and the
// Webinar Projections tab (Sheets view → Webinar Actuals: its "Actual" column and the yellow inputs it pre-fills).
// Joey, Oct 29 2026 11:05 PM ET: "We need to establish a source of truth ... When I go to the W011 webinar, they need to be the exact
// same. There needs to be congruency between the Webinar Projections, the actual Webinars tab, and Callix."
// Joey, 11:07 PM ET: cost per booking is "just based on how much we spent on ads and then our return on ... calls from ads" →
// ad spend ÷ PAID webinar bookings on Paid AND Blended/All; Organic has no ad spend ("—"), like CPR.
// Both tabs read the same /api/webinar2/enhanced/:id?traffic=all|paid|organic response and pass it here; labels and number formats
// come from here too, so the same label always shows the same figure.
//
// Definitions (Joey's rules, memory uu-attribution-rules):
//  - Paid / Organic = each person's MOST RECENT opt-in's UTMs. "all" = Blended.
//  - Cost per registrant (CPR) = ad spend ÷ PAID opt-ins (on Paid and Blended). Organic: —.
//  - Cost per booking = ad spend ÷ PAID webinar bookings (on Paid and Blended). Organic: —.
//  - ROAS = the view's cash ÷ ad spend (Paid = paid cash, Blended = all cash). Organic: —.
//  - Opt-in rate = opt-ins ÷ landing page visitors (Paid: Meta landing page views).
//  - Webinar show rate = attended ÷ opt-ins. Retention at pitch = people at pitch ÷ attended.
//  - Application rate = applications ÷ people at pitch. Booking rate = webinar bookings ÷ people at pitch (both: every application /
//    booking tied to the webinar, during it and after it — the same counts as the Applications / Webinar Bookings cards).
//  - Application-to-booking rate = webinar bookings ÷ applications.
//  - End of Call Reports filed = booked calls with an outcome logged; "Bookings with an EOC" = EOCs ÷ webinar bookings (so far).
//  - Call show rate = calls taken (deposits included: the call was taken) ÷ (calls taken + no-shows). "Cancelled by sales team" /
//    cancelled / rescheduled EOCs are neutral: in neither (Joey, Nov 2 2026 9:11 PM ET).
//  - Closes = closed only. Deposits are NEUTRAL for close rate (out of the top and the bottom): close rate = closed ÷ (calls taken −
//    deposits); cash per close = cash ÷ closed. Joey, Nov 2 2026 9:11 PM ET: "deposits don't increase or decrease our close rate.
//    They just stay flat. They're basically the same exact thing as canceled by sales team. It does positively increase the show rate".
//  - Cost per application / cost per close (CAC) = ad spend ÷ PAID applications / PAID closes (the same "ads" rule; Blended needs the
//    Paid response passed as `paid`, otherwise they stay —).

export type WebinarSegment = "all" | "paid" | "organic";

export interface WebinarSalesBlock {
  optins: number; apps: number; fullApps?: number; partialApps?: number; qualifiedApps?: number;
  bookedCalls: number; callsDue: number; takenCalls: number; closed: number; deposits?: number; noShows?: number;
  closeRate: number; cash: number; costPerBooking?: number;
}
export interface WebinarMetricsSource {
  adSpend: number; landingPageVisitors?: number | null;
  salesTotal: WebinarSalesBlock;
  onWebinar: { cpr?: number; registrants?: number; attended: number; attendeesAtPitch: number; retentionAtPitch?: number };
}

export type WebinarMetricKey =
  | "spend" | "optins" | "paidOptins" | "lpVisitors" | "optinRate" | "cpr" | "attended" | "webinarShowRate" | "retention" | "atPitch"
  | "apps" | "appRate" | "booked" | "paidBooked" | "bookingRate" | "appToBooking" | "costPerApp" | "costPerBooking" | "eocs" | "eocShare"
  | "taken" | "callShowRate" | "closes" | "closeRate" | "cash" | "cashPerClose" | "costPerClose" | "roas";

export type WebinarMetrics = Record<WebinarMetricKey, number | null> & { segment: WebinarSegment; deposits: number; noShows: number };

/** The one label per metric, used verbatim on both tabs. */
export const WEBINAR_METRIC_LABEL: Record<WebinarMetricKey, string> = {
  spend: "Ad Spend",
  optins: "Opt-ins",
  paidOptins: "Paid Opt-ins",
  lpVisitors: "Landing Page Visitors",
  optinRate: "Opt-in Rate",
  cpr: "Cost per Registrant (CPR)",
  attended: "Attended",
  webinarShowRate: "Webinar Show Rate",
  retention: "Retention at Pitch",
  atPitch: "People at Pitch",
  apps: "Applications",
  appRate: "Application Rate",
  booked: "Webinar Bookings",
  paidBooked: "Paid Webinar Bookings",
  bookingRate: "Booking Rate",
  appToBooking: "Application-to-Booking Rate",
  costPerApp: "Cost / Application (ads)",
  costPerBooking: "Cost / Booked Call (ads)",
  eocs: "End of Call Reports Filed",
  eocShare: "Bookings with an EOC (so far)",
  taken: "Calls Taken",
  callShowRate: "Call Show Rate",
  closes: "Closes",
  closeRate: "Close Rate",
  cash: "Cash Collected",
  cashPerClose: "Cash per Close",
  costPerClose: "Cost / Close (CAC, ads)",
  roas: "ROAS",
};

export type WebinarMetricKind = "count" | "money" | "rate" | "ratio";
export const WEBINAR_METRIC_KIND: Record<WebinarMetricKey, WebinarMetricKind> = {
  spend: "money", optins: "count", paidOptins: "count", lpVisitors: "count", optinRate: "rate", cpr: "money", attended: "count",
  webinarShowRate: "rate", retention: "rate", atPitch: "count", apps: "count", appRate: "rate", booked: "count", paidBooked: "count",
  bookingRate: "rate", appToBooking: "rate", costPerApp: "money", costPerBooking: "money", eocs: "count", eocShare: "rate", taken: "count",
  callShowRate: "rate", closes: "count", closeRate: "rate", cash: "money", cashPerClose: "money", costPerClose: "money", roas: "ratio",
};
/** Unit costs shown to the cent (so they can be checked against Callix Ads Manager to the cent). */
export const WEBINAR_CENTS_KEYS = new Set<string>(["cpr", "costPerApp", "costPerBooking", "costPerClose",
  // Sheets-view keys for the same unit costs
  "cpc", "costPerAttendee", "costPerBooked", "cpa"]);

const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num1 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

/** The one number format: money whole dollars (unit costs and anything under $20 to the cent), rates one decimal ("100%", "43.9%"),
 *  ROAS two decimals ("2.57x"), counts with commas. "—" when there is no value. */
export function fmtWebinarValue(kind: WebinarMetricKind, v: number | null | undefined, cents = false): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (kind === "money") return v === 0 ? "$0" : cents || Math.abs(v) < 20 ? usd2.format(v) : usd0.format(v);
  if (kind === "rate") { const r1 = Math.round(v * 10) / 10; return `${Number.isInteger(r1) ? r1.toFixed(0) : r1.toFixed(1)}%`; }
  if (kind === "ratio") return `${v.toFixed(2)}x`;
  return num1.format(v);
}
export function fmtWebinarMetric(key: WebinarMetricKey, v: number | null | undefined): string {
  return fmtWebinarValue(WEBINAR_METRIC_KIND[key], v, WEBINAR_CENTS_KEYS.has(key));
}

const per = (a: number, d: number | null | undefined): number | null => (d !== null && d !== undefined && d > 0 && a > 0 ? a / d : null);
const pct = (a: number | null | undefined, d: number | null | undefined): number | null => (a !== null && a !== undefined && d !== null && d !== undefined && d > 0 ? (a / d) * 100 : null);

/**
 * @param d       the /api/webinar2/enhanced response for this segment
 * @param segment "all" (Blended) | "paid" | "organic"
 * @param opts.block  a sales block other than salesTotal (Webinars tab's On-webinar / Post-webinar toggle); same formulas
 * @param opts.paid   the Paid response's salesTotal (+ its adSpend), so Blended divides cost per application / close by paid counts
 *                    and uses the same ad spend as the Paid view
 */
export function webinarMetrics(d: WebinarMetricsSource, segment: WebinarSegment, opts: { block?: WebinarSalesBlock; paid?: (WebinarSalesBlock & { adSpend?: number }) | null } = {}): WebinarMetrics {
  const s = opts.block ?? d.salesTotal;
  // ONE ad spend per webinar: Blended uses the Paid response's spend when it has it. Spend is live from Meta and the All / Paid
  // responses are cached separately, so they could differ by a few cents for a few minutes (Oct 30: $4,280.84 vs $4,280.81),
  // which put a 1-cent gap between Paid and Blended cost per close. Same spend → Paid and Blended ad metrics are identical.
  const spend = segment === "organic" ? 0 : segment === "all" && (opts.paid?.adSpend ?? 0) > 0 ? (opts.paid!.adSpend as number) : d.adSpend || 0;
  const vis = d.landingPageVisitors || 0;
  const att = d.onWebinar.attended || 0;
  const pitch = d.onWebinar.attendeesAtPitch || 0;
  const deposits = s.deposits || 0;
  const noShows = s.noShows || 0;
  const closes = s.closed; // deposits are neutral (Joey, Nov 2 2026)
  const closeBase = Math.max(0, s.takenCalls - deposits); // calls taken minus deposits
  // Paid counts behind the ad-spend metrics. Paid view: the view's own counts. Blended: the server's paid counts (CPR is computed on paid
  // opt-ins, cost per booking on paid webinar bookings, so the paid count = spend ÷ that cost), or the Paid response when passed.
  const paidOptins = segment === "paid" ? d.salesTotal.optins : segment === "all" && (d.onWebinar.cpr ?? 0) > 0 && spend > 0 ? Math.round(spend / (d.onWebinar.cpr as number)) : null;
  const serverCpb = d.salesTotal.costPerBooking ?? 0;
  const paidBooked = segment === "paid" ? d.salesTotal.bookedCalls : segment === "all" ? (opts.paid ? opts.paid.bookedCalls : serverCpb > 0 && spend > 0 ? Math.round(spend / serverCpb) : null) : null;
  const paidApps = segment === "paid" ? d.salesTotal.apps : segment === "all" && opts.paid ? opts.paid.apps : null;
  const paidCloses = segment === "paid" ? closes : segment === "all" && opts.paid ? opts.paid.closed : null;
  const ads = segment !== "organic" && spend > 0;
  return {
    segment, deposits, noShows,
    spend: segment === "organic" ? null : spend,
    optins: s.optins,
    paidOptins,
    lpVisitors: vis > 0 ? vis : null,
    optinRate: pct(s.optins, vis),
    cpr: ads ? per(spend, paidOptins) : null,
    attended: att,
    webinarShowRate: pct(att, d.salesTotal.optins),
    retention: pct(pitch, att),
    atPitch: pitch,
    apps: s.apps,
    appRate: pct(s.apps, pitch),
    booked: s.bookedCalls,
    paidBooked,
    bookingRate: pct(s.bookedCalls, pitch),
    appToBooking: pct(s.bookedCalls, s.apps),
    costPerApp: ads ? per(spend, paidApps) : null,
    costPerBooking: ads ? per(spend, paidBooked) : null,
    eocs: s.callsDue,
    eocShare: pct(s.callsDue, s.bookedCalls),
    taken: s.takenCalls,
    callShowRate: pct(s.takenCalls, s.takenCalls + noShows),
    closes,
    closeRate: closeBase > 0 ? pct(closes, closeBase) : null,
    cash: s.cash,
    cashPerClose: closes > 0 ? s.cash / closes : null,
    costPerClose: ads ? per(spend, paidCloses) : null,
    roas: ads ? s.cash / spend : null,
  };
}

/** "a ÷ b" helper texts both tabs put under a card / in a row note, so the denominators read the same. */
export function webinarMetricHow(key: WebinarMetricKey, m: WebinarMetrics): string {
  const n = (v: number | null | undefined) => (v === null || v === undefined ? "—" : num1.format(v));
  const $ = (v: number | null | undefined) => fmtWebinarValue("money", v);
  switch (key) {
    case "optinRate": return `${n(m.optins)} ÷ ${n(m.lpVisitors)} ${m.segment === "paid" ? "Meta LP views" : "LP visitors"}`;
    case "cpr": return m.segment === "organic" ? "no ad spend on organic" : `${$(m.spend)} ÷ ${n(m.paidOptins)} paid opt-ins`;
    case "webinarShowRate": return `${n(m.attended)} attended ÷ ${n(m.optins)} opt-ins`;
    case "retention": return `${n(m.atPitch)} at pitch ÷ ${n(m.attended)} attended`;
    case "appRate": return `${n(m.apps)} apps ÷ ${n(m.atPitch)} at pitch`;
    case "bookingRate": return `${n(m.booked)} booked ÷ ${n(m.atPitch)} at pitch`;
    case "appToBooking": return `${n(m.booked)} booked ÷ ${n(m.apps)} apps`;
    case "costPerBooking": return m.segment === "organic" ? "no ad spend on organic" : `${$(m.spend)} ÷ ${n(m.paidBooked)} paid webinar bookings`;
    case "costPerApp": return m.segment === "organic" ? "no ad spend on organic" : m.costPerApp === null ? "needs paid applications" : `${$(m.spend)} ÷ paid applications`;
    case "costPerClose": return m.segment === "organic" ? "no ad spend on organic" : m.costPerClose === null ? "no paid closes yet" : `${$(m.spend)} ÷ paid closes`;
    case "eocShare": return `${n(m.eocs)} of ${n(m.booked)} bookings have an EOC`;
    case "callShowRate": return `${n(m.taken)} taken ÷ (${n(m.taken)} taken + ${n(m.noShows)} no-shows)${m.deposits ? ` · deposits count as taken` : ""}`;
    case "closeRate": return m.deposits ? `${n(m.closes)} closed ÷ (${n(m.taken)} taken − ${n(m.deposits)} deposit${m.deposits === 1 ? "" : "s"})` : `${n(m.closes)} closed ÷ ${n(m.taken)} taken`;
    case "cashPerClose": return `${$(m.cash)} ÷ ${n(m.closes)} close${m.closes === 1 ? "" : "s"}`;
    case "roas": return m.segment === "organic" ? "no ad spend on organic" : `${$(m.cash)} ${m.segment === "paid" ? "paid" : "all"} cash ÷ ${$(m.spend)}`;
    default: return "";
  }
}
